const px = (id, w = 1200) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;
const us = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=70`;

export const IMAGES = {
  hero: px(13993704, 1900),
  philosophy: us("1517836357463-d25dfeac3438", 1200),
  finalCta: us("1574680096145-d05b474e2155", 1900),
};

export const HERO_WORDS = ["PUSH.", "PULL.", "SQUAT.", "LIFT.", "ROTATE.", "ENDURE."];

export const CAPABILITIES = [
  {
    id: "push",
    num: "01",
    title: "PUSH",
    icon: "MoveUpRight",
    desc: "Build upper-body strength and the ability to move resistance away from the body.",
    exercises: ["Dumbbell Press", "Push-Up Variations", "Landmine Press"],
    image: us("1541534741688-6078c6bfb5c5", 1000),
  },
  {
    id: "pull",
    num: "02",
    title: "PULL",
    icon: "Grip",
    desc: "Develop back, arm and grip strength for pulling and controlling resistance.",
    exercises: ["TRX Row", "Kettlebell High Pull", "Band Pull-Apart"],
    image: us("1526506118085-60ce8714f8c5", 1000),
  },
  {
    id: "squat",
    num: "03",
    title: "SQUAT",
    icon: "ArrowDownToLine",
    desc: "Improve lower-body strength, mobility and everyday movement mechanics.",
    exercises: ["Goblet Squat", "Split Squat", "Squat-to-Stand"],
    image: px(18986400, 1000),
  },
  {
    id: "bend",
    num: "04",
    title: "BEND & LIFT",
    icon: "Package",
    desc: "Learn efficient hip movement and build the strength needed to safely lift objects.",
    exercises: ["Kettlebell Deadlift", "Hip Hinge Drill", "Single-Leg RDL"],
    image: px(4853280, 1000),
  },
  {
    id: "rotation",
    num: "05",
    title: "ROTATION",
    icon: "RotateCw",
    desc: "Develop rotational strength, core control and movement across multiple planes.",
    exercises: ["Med-Ball Rotational Throw", "Band Chop", "Rotational Lunge"],
    image: us("1594381898411-846e7d193883", 1000),
  },
  {
    id: "endurance",
    num: "06",
    title: "ENDURANCE",
    icon: "Infinity",
    desc: "Improve the ability to move, work and perform for longer.",
    exercises: ["Loaded Carries", "Conditioning Circuits", "Agility Intervals"],
    image: us("1461896836934-ffe607ba8211", 1000),
  },
];

export const TRAINING_PANELS = [
  { word: "STRENGTH", image: us("1534438327276-14e5300c3a48", 1200), tag: "Dumbbells / Kettlebells / Plates" },
  { word: "CONTROL", image: us("1571902943202-507ec2618e8f", 1200), tag: "Tempo / Balance / Breath" },
  { word: "MOBILITY", image: us("1583454110551-21f2fa2afe61", 1200), tag: "Hips / Spine / Shoulders" },
  { word: "COORDINATION", image: us("1434682881908-b43d0467b798", 1200), tag: "Agility Ladder / Footwork / Reaction" },
  { word: "ENDURANCE", image: us("1546483875-ad9014c88eba", 1200), tag: "Circuits / Carries / Conditioning" },
  { word: "MOVEMENT", image: us("1518611012118-696072aa579a", 1200), tag: "Crawl / Jump / Throw / Sprint" },
];

export const SESSION_STEPS = [
  { num: "01", title: "PREPARE", desc: "Warm-up + Mobility + Movement Preparation", detail: "Raise the temperature, open the joints, switch on the nervous system." },
  { num: "02", title: "TRAIN", desc: "Primary Capability Training", detail: "The day's headline capability — pushed, pulled, squatted or lifted with intent." },
  { num: "03", title: "MOVE", desc: "Functional Circuits + Athletic Movement", detail: "Carries, throws, slams and agility work that tie strength to athleticism." },
  { num: "04", title: "CONTROL", desc: "Core + Coordination + Animal Movement", detail: "Own your body in space — crawls, balances and reactive patterns." },
  { num: "05", title: "RECOVER", desc: "Mobility + Static Recovery", detail: "Downshift, breathe, restore range. Leave better than you arrived." },
];

export const DIAGRAM_NODES = [
  { label: "STRENGTH", desc: "The force behind every push, pull, squat and lift — built progressively, never recklessly." },
  { label: "ENDURANCE", desc: "The capacity to keep moving well, long after others have to stop." },
  { label: "CARDIOVASCULAR FITNESS", desc: "A stronger heart and lungs for stairs, sprints and everything in between." },
  { label: "FLEXIBILITY", desc: "Usable range of motion — muscles that lengthen as well as they contract." },
  { label: "MOBILITY", desc: "Joints that move freely and under control, through the positions life demands." },
  { label: "BALANCE", desc: "Stability on one leg, on uneven ground, in unpredictable moments." },
  { label: "COORDINATION", desc: "Eyes, hands and feet working as one — the hallmark of an athletic body." },
];

export const LIFE_PAIRS = [
  { gym: "DEADLIFT", life: "LIFTING LUGGAGE", gymImg: px(4853280, 1000), lifeImg: us("1574202141112-c3a90e1a3ce2", 1000) },
  { gym: "FARMER CARRY", life: "CARRYING GROCERIES", gymImg: us("1599058917212-d750089bc07e", 1000), lifeImg: px(7345419, 1000) },
  { gym: "STEP-UP", life: "CLIMBING STAIRS", gymImg: us("1550345332-09e3ac987658", 1000), lifeImg: px(14741974, 1000) },
  { gym: "ROTATIONAL POWER", life: "PLAYING SPORT", gymImg: us("1594381898411-846e7d193883", 1000), lifeImg: px(17769342, 1000) },
];

export const PROFILES = [
  { num: "01", title: "BEGINNERS", desc: "Build movement confidence and strong foundations — no experience required, just intent." },
  { num: "02", title: "ACTIVE ADULTS", desc: "Become stronger, fitter and more capable in the body you live in every day." },
  { num: "03", title: "FITNESS ENTHUSIASTS", desc: "Add athleticism, mobility and functional performance to the training you already love." },
  { num: "04", title: "MOVE BETTER, LONGER", desc: "Train for longevity and everyday performance — stay capable for the decades ahead." },
];

export const GALLERY = [
  { src: "/images/gallery/1.png", alt: "Small group kettlebell session in the studio", span: "md:col-span-7 md:row-span-2" },
  { src: "/images/gallery/2.png", alt: "Coach correcting deadlift hip-hinge form", span: "md:col-span-5" },
  { src: "/images/gallery/3.png", alt: "Medicine ball rotational throw power work", span: "md:col-span-5" },
  { src: "/images/gallery/4.png", alt: "Partner bear-crawl animal movement drill", span: "md:col-span-4" },
  { src: "/images/gallery/5.png", alt: "Chalked hands gripping a kettlebell", span: "md:col-span-4" },
  { src: "/images/gallery/6.png", alt: "Deep squat mobility and recovery work", span: "md:col-span-4" },
];

export const TESTIMONIALS = [
  { quote: "I move better. Full stop.", context: "On everyday movement" },
  { quote: "Everyday tasks feel easier — groceries, stairs, all of it.", context: "On daily life" },
  { quote: "I feel stronger without feeling stiff.", context: "On strength & mobility" },
  { quote: "My balance and coordination genuinely improved.", context: "On control" },
  { quote: "Training finally feels useful.", context: "On purpose" },
];

export const ASSESSMENT_METRICS = [
  { label: "Movement Quality", value: 82 },
  { label: "Strength", value: 74 },
  { label: "Endurance", value: 68 },
  { label: "Mobility", value: 77 },
  { label: "Balance", value: 85 },
  { label: "Coordination", value: 71 },
];

export const NAV_LINKS = [
  { label: "The Method", href: "#method" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Programs", href: "#programs" },
  { label: "Gallery", href: "#gallery" },
];
